import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PaymentPixInstructionsBody } from '../../components/payment/payment-pix-instructions-body';
import { PaymentPixQRCodeModalTitle } from '../../components/payment/payment-pix-qrcode-modal-title';
import { useErrorHandler } from '../../hooks/use-error-handler';
import { usePaymentInterval } from '../../hooks/use-payment-interval';
import { useStorage } from '../../hooks/use-storage';
import { useSwal } from '../../hooks/use-swal';
import { usePaymentPixManager } from '../../utils/manager/payment-pix-manager';
import { useAnalytics } from '../../utils/setup/analytics-setup';

function usePaymentPixInstructionsModal() {
  const storage = useStorage();
  const { t } = useTranslation();
  const { paymentRef } = storage;
  const { analyticsPageView, analyticsAlertEvent, analyticsHollowEvent } =
    useAnalytics();
  const { handleErrorOnModal } = useErrorHandler();
  const { swal } = useSwal({ storage });
  const { refreshPaymentPix } = usePaymentPixManager();

  const [loading, setLoading] = useState(false);
  const [focused, setFocused] = useState(false);
  const ordersRef = useRef();
  const tipAmountRef = useRef();
  const wasCancelledRef = useRef(false);
  const wasPaidRef = useRef(false);

  const onPaymentPaid = async () => {
    wasPaidRef.current = true;
    swal.close();
  };

  const onPaymentCancelled = async () => {
    wasCancelledRef.current = true;
    swal.close();
  };

  usePaymentInterval(
    {
      payment: paymentRef.current,
      orders: ordersRef.current,
      onPaymentPaid,
      onPaymentCancelled,
    },
    focused && !loading,
    [focused, loading]
  );

  const didOpen = () => {
    analyticsPageView('Payment Pix Instructions');
    setFocused(true);
  };

  const didDestroy = () => {
    setFocused(false);
  };

  async function prepare({ orders, tipAmount }) {
    ordersRef.current = orders;
    tipAmountRef.current = tipAmount;
  }

  async function show({ copied, prepareOnPreConfirm, prepareOnPreDeny }) {
    const result = await swal.fire({
      title: (
        <PaymentPixQRCodeModalTitle
          title={t('payment-pix-instructions-body.title')}
          subTitle={t('payment-pix-instructions-body.copied')}
        />
      ),
      icon: 'success',
      html: (
        <PaymentPixInstructionsBody
          analyticsHollowEvent={analyticsHollowEvent}
          showPayload={!copied}
        />
      ),
      didOpen,
      didDestroy,
      preConfirm: () => preConfirm({ prepareOnPreConfirm }),
      preDeny: () => preDeny({ prepareOnPreDeny }),
      showDenyButton: true,
      showCloseButton: false,
      showLoaderOnConfirm: true,
      showLoaderOnDeny: true,
      confirmButtonText: t(
        'payment-pix-instructions-body.payment.confirmation'
      ),
      denyButtonText: t('back'),
    });

    if (result.isDenied) {
      result.wasBackButtonClicked = true;
    }

    if (wasCancelledRef.current) {
      result.isDismissed = true;
      result.isConfirmed = false;
      result.wasCancelled = true;
    }

    if (wasPaidRef.current) {
      result.isDismissed = false;
      result.isConfirmed = true;
    }

    if (result.isConfirmed) {
      result.wasPaid = true;
    }

    return result;
  }

  const preConfirm = async ({ prepareOnPreConfirm }) => {
    setLoading(true);

    try {
      await refreshPaymentPix({
        payment: paymentRef.current,
        orders: ordersRef.current,
      });

      if (paymentRef.current.status !== 'paid') {
        swal.showValidationMessage(
          t('payment-pix-instructions-body.payment.not-confirmed')
        );
        analyticsAlertEvent('payment not confirmed');

        setLoading(false);
        return false;
      }

      await prepareOnPreConfirm?.call(this);
    } catch (error) {
      handleErrorOnModal(error);

      setLoading(false);
      return false;
    }

    setLoading(false);
    return true;
  };

  const preDeny = async ({ prepareOnPreDeny }) => {
    setLoading(true);

    try {
      await prepareOnPreDeny?.call(this);
    } catch (error) {
      handleErrorOnModal(error);

      setLoading(false);
      return false;
    }

    setLoading(false);
    return true;
  };

  return {
    preparePaymentPixInstructionsModal: prepare,
    showPaymentPixInstructionsModal: show,
  };
}

export { usePaymentPixInstructionsModal };
